import type { InspectionSchedule } from '../inspection-template/inspection-template.interface';
import type { BaseDocument } from '../mongo/base-document.interface';

/**
 * Where an inspection is in its life (SWOOP-391). There is no "overdue" or "missed" status: overdue is
 * derived from `dueOn`.
 */
export const INSPECTION_STATUS = { COMPLETED: 'completed', DUE: 'due', IN_PROGRESS: 'in_progress' } as const;

export type InspectionStatus = (typeof INSPECTION_STATUS)[keyof typeof INSPECTION_STATUS];

/** Every status, for validators and tab maps (SWOOP-400). */
export const INSPECTION_STATUSES: InspectionStatus[] = Object.values(INSPECTION_STATUS);

/** How an inspection came to exist (SWOOP-391): generated by the schedule, or started by hand. */
export const INSPECTION_SOURCE = { ON_DEMAND: 'on_demand', SCHEDULED: 'scheduled' } as const;

export type InspectionSource = (typeof INSPECTION_SOURCE)[keyof typeof INSPECTION_SOURCE];

/** The intervals that generate due inspections: every schedule but `on_demand`. */
export type ScheduledInterval = Exclude<InspectionSchedule, 'on_demand'>;

/**
 * One check of one vehicle against one template, as stored in `inspections` (SWOOP-391). A `due` row is
 * created, moved or removed by the schedule sync; the start and completion fields are set by later tickets.
 */
export interface InspectionRecord extends BaseDocument {
  /** When it was completed; the next due date counts from `toCalendarDay(completedAt)`. */
  completedAt: Date | null;
  /** UTC midnight of the business day it fell due; `null` for an on-demand inspection. */
  dueOn: Date | null;
  /** The template's interval when the row was created, kept if the template changes later; `null` on demand. */
  schedule: ScheduledInterval | null;
  source: InspectionSource;
  startedAt: Date | null;
  /** ObjectId in storage: the user who started it. */
  startedBy: string | null;
  status: InspectionStatus;
  /** ObjectId in storage. */
  templateId: string;
  /** The template version it started with; `null` while still `due`. */
  templateVersion: number | null;
  /** ObjectId in storage. */
  vehicleId: string;
}

export type CreateInspection = Omit<InspectionRecord, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;
